import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Link } from '@mui/material';
import termsContent from './terms-of-use.json';
import PrivateFooterSection from '../landing/sections/FooterSection';
import '../landing/LandingPage.css';
import useAuth from '../../common/hooks/useAuth';
import { useState, useEffect } from 'react';

const renderContentWithBold = (content) => {
  const parts = content.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <span key={index} style={{ fontWeight: 700 }}>{part.slice(2, -2)}</span>;
    }
    return part;
  });
};

const renderContent = (content) => {
  return content.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      {renderContentWithBold(line)}
      {index < content.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));
};

export default function PrivateTermsOfUse() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const { auth } = useAuth();

    useEffect(() => {
        if (auth?.accessToken) {
        setIsAuthenticated(true);
        } else {
        setIsAuthenticated(false);
        }
    }, [auth?.accessToken]);

  useEffect(() => {
    document.title = "Terms of Use - Vtrender™";
  }, []);

  return (
        <>
    <div className="landing-page">
      {/* Header component */}
      <Box
        className="privacy-content"
        sx={{ marginTop: 10, marginLeft: 3, marginBottom: 10, marginRight: 2 }}
        align="justify"
      >
        {/* Privacy Policy Section */}
       <div className="main-policy-section" id='privacy-policy'>
        <div className="policy-section">
          <Typography
            id="privacy-policy-title"
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h4"
          >
            {termsContent.privacyPolicy.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.privacyPolicy.content)}
            <br />
            <br />
            If you have any questions or concerns about our privacy policy, please
            contact us at{" "}
            <Link
              className="contact-link"
              href="mailto:support@vtrender.com"
            >
              support@vtrender.com
            </Link>
          </Typography>
        </div>
      </div>

        {/* Refund Policy Section */}
      <div className="main-policy-section" id='refund-policy'>
        <div className="policy-section">
          <Typography
            id="refund-policy-title"
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h4"
          >
            {termsContent.refundPolicy.title1}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.refundPolicy.content1)}
          </Typography>
        </div>

        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h6"
          >
            {termsContent.refundPolicy.title2}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.refundPolicy.content2)}
          </Typography>
        </div>
      </div>
      
        {/* Main Policy Section */}
      <div className="main-policy-section" id='terms-of-service'>
        {/* Terms of Use Section */}
        <div className="policy-section">
          <Typography
            id="terms-of-use-title"
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h4"
          >
            {termsContent.termsOfUse.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.introduction)}
          </Typography>
        </div>

        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 700 }}
            variant="subtitle1"
          >
            {termsContent.termsOfUse.pleaseRead}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.agreement)}
          </Typography>
        </div>

        {/* Ownership */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.ownership.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.ownership.content)}
          </Typography>
        </div>

        {/* Disclaimer */}
        <div className="policy-section disclaimer-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.disclaimer.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.disclaimer.content)}
          </Typography>
        </div>

        {/* Cancellations & Refunds */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h6"
          >
            {termsContent.termsOfUse.sections.cancellationsRefunds.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.cancellationsRefunds.content)}
          </Typography>
        </div>

        {/* License to Use */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h6"
          >
            {termsContent.termsOfUse.sections.licenseToUse.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.licenseToUse.content)}
          </Typography>
        </div>

        {/* Limitations of Rights */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h6"
          >
            {termsContent.termsOfUse.sections.limitationsOfRights.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.limitationsOfRights.content)}
          </Typography>
        </div>

        {/* Materials Provided */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h6"
          >
            {termsContent.termsOfUse.sections.materialsProvided.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.materialsProvided.content)}
          </Typography>
        </div>

        {/* Privacy Policy in Terms */}
        <div className="policy-section contact-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.privacyPolicyInTerms.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.privacyPolicyInTerms.content)}
            <br />
            <br />
            If you have any questions or concerns about our privacy policy, please
            contact us at{" "}
            <Link
              className="contact-link"
              href="mailto:support@vtrender.com"
            >
              support@vtrender.com
            </Link>
          </Typography>
        </div>

        {/* Edits to Content */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.editsToContent.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.editsToContent.content)}
          </Typography>
        </div>

        {/* Copyright */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.copyrightInfringement.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.copyrightInfringement.content)}
          </Typography>
        </div>

        {/* Warranty Disclaimer */}
        <div className="policy-section disclaimer-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.disclaimerOfWarranty.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.disclaimerOfWarranty.content)}
          </Typography>
        </div>

        {/* Liability Limitation */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.limitationOfLiability.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.limitationOfLiability.content)}
          </Typography>
        </div>

        {/* Release */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.release.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.release.content)}
          </Typography>
        </div>

        {/* Arbitration */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.arbitration.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.arbitration.content)}
          </Typography>
        </div>

        {/* Miscellaneous */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.miscellaneous.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.miscellaneous.content)}
          </Typography>
        </div>

        {/* No Refund Policy */}
        <div className="policy-section">
          <Typography
            className="section-title-new"
            sx={{ fontWeight: 700 }}
            variant="h5"
          >
            {termsContent.termsOfUse.sections.noRefundPolicyInTerms.title}
          </Typography>
          <Typography className="section-content">
            {renderContent(termsContent.termsOfUse.sections.noRefundPolicyInTerms.content)}
          </Typography>
        </div>
      </div>

        <br />
        <br />
        <br />
        <br />
      </Box>
      <PrivateFooterSection />
    </div>
  </>
  );
}